import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import { CrmProfileSectionsCard, getCrmProfileToneClasses } from "@/components/crm/profile-sections-card";
import { cn } from "@/lib/utils";
import { getCrmActionHrefKind, shouldOpenCrmActionHrefInNewTab } from "@ease/crm/safe-action-hrefs";
import type { CrmTaskAction, CrmTaskActionGroup, CrmTaskActionTone } from "@ease/crm/task-action-groups";
import type {
  CrmTaskChecklistItem,
  CrmTaskChecklistTone,
  CrmTaskExecutionChecklist,
  CrmTaskExecutionChecklistSummary,
} from "@ease/crm/task-execution-checklist";
import type { CrmTaskProfileSection, CrmTaskProfileSectionSummary } from "@ease/crm/task-profile-sections";
import type {
  CrmTaskPlaybook,
  CrmTaskPlaybookItem,
  CrmTaskPlaybookItemKind,
  CrmTaskPlaybookSummary,
  CrmTaskPlaybookTone,
} from "@ease/crm/task-playbooks";

export function CrmTaskProfileSectionsPanel({ sections }: { sections: CrmTaskProfileSection[] }) {
  return (
    <CrmProfileSectionsCard
      sections={sections}
      title="Task intelligence"
      description="Configured task evidence from tenant, product, migration, and automation context."
      className="mb-3 rounded-md p-3"
      titleClassName="font-semibold tracking-[0.12em]"
      descriptionClassName="text-xs"
      itemClassName="rounded-md bg-[var(--color-surface)] px-2 py-1.5"
      actionClassName="px-2 py-0.5 text-[11px] hover:bg-[var(--color-background)]"
    />
  );
}

export function CrmTaskActionGroupsPanel({ groups }: { groups: CrmTaskActionGroup[] }) {
  if (groups.length === 0) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Task actions
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Tenant-configured review links. Buttons only render for safe internal, HTTPS, phone, SMS, or email destinations.
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-muted-foreground)]">
          {groups.reduce((total, group) => total + group.actions.length, 0)}
        </span>
      </div>
      <div className="mt-3 space-y-3">
        {groups.map((group) => (
          <section key={group.key} className="border-t border-[var(--color-border)] pt-3 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{group.label}</h3>
              {group.description ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">{group.description}</p>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {group.actions.map((action) => (
                <CrmTaskActionLink key={`${group.key}:${action.key}`} action={action} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function CrmTaskPlaybooksPanel({ playbooks }: { playbooks: CrmTaskPlaybook[] }) {
  if (playbooks.length === 0) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Task playbook
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Tenant-configured talk tracks, questions, objection handling, and success criteria.
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-muted-foreground)]">
          {playbooks.reduce((total, playbook) => total + playbook.items.length, 0)}
        </span>
      </div>
      <div className="mt-3 space-y-3">
        {playbooks.map((playbook) => (
          <section key={playbook.key} className="border-t border-[var(--color-border)] pt-3 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{playbook.label}</h3>
              {playbook.summary ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">{playbook.summary}</p>
              ) : null}
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {playbook.items.map((item) => (
                <CrmTaskPlaybookItemCell key={`${playbook.key}:${item.key}`} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function CrmTaskPlaybookItemCell({ item }: { item: CrmTaskPlaybookItem }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">{item.label}</p>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)]">
            {formatCrmTaskPlaybookKind(item.kind)}
          </span>
          {item.tone !== "neutral" ? (
            <span className={cn(
              "rounded-full border px-1.5 py-0.5 text-[10px] font-medium capitalize",
              getCrmTaskPlaybookToneClasses(item.tone),
            )}>
              {item.tone}
            </span>
          ) : null}
        </div>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-xs text-[var(--color-foreground)]">{item.detail}</p>
    </div>
  );
}

export function CrmTaskExecutionChecklistsPanel({ checklists }: { checklists: CrmTaskExecutionChecklist[] }) {
  if (checklists.length === 0) return null;

  return (
    <div className="mb-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
            Task checklist
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Tenant-configured operator steps and verification checks for completing this task.
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-muted-foreground)]">
          {checklists.reduce((total, checklist) => total + checklist.items.length, 0)}
        </span>
      </div>
      <div className="mt-3 space-y-3">
        {checklists.map((checklist) => (
          <section key={checklist.key} className="border-t border-[var(--color-border)] pt-3 first:border-t-0 first:pt-0">
            <div className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">{checklist.label}</h3>
              {checklist.description ? (
                <p className="text-xs text-[var(--color-muted-foreground)]">{checklist.description}</p>
              ) : null}
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {checklist.items.map((item) => (
                <CrmTaskChecklistItemCell key={`${checklist.key}:${item.key}`} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function CrmTaskChecklistItemCell({ item }: { item: CrmTaskChecklistItem }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--color-muted-foreground)]">{item.label}</p>
          {item.required ? (
            <p className="mt-0.5 text-[10px] uppercase tracking-[0.12em] text-[var(--color-warning)]">Required</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1">
          {item.status ? (
            <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)]">
              {formatCrmTaskLabel(item.status) ?? item.status}
            </span>
          ) : null}
          {item.tone !== "neutral" ? (
            <span className={cn(
              "rounded-full border px-1.5 py-0.5 text-[10px] font-medium capitalize",
              getCrmTaskChecklistToneClasses(item.tone),
            )}>
              {item.tone}
            </span>
          ) : null}
        </div>
      </div>
      {item.description ? (
        <p className="mt-1 break-words text-xs text-[var(--color-foreground)]">{item.description}</p>
      ) : null}
      {item.href ? <CrmTaskChecklistItemLink item={item} /> : null}
    </div>
  );
}

function CrmTaskChecklistItemLink({ item }: { item: CrmTaskChecklistItem }) {
  if (!item.href) return null;
  const className = "mt-2 inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-background)]";
  const content = (
    <>
      <span>{item.actionLabel ?? "Open"}</span>
      <ArrowUpRight className="h-3 w-3" />
    </>
  );
  const hrefKind = getCrmActionHrefKind(item.href);
  if (hrefKind === "internal") {
    return (
      <Link to={item.href} className={className}>
        {content}
      </Link>
    );
  }
  if (!hrefKind) return null;

  const openInNewTab = shouldOpenCrmActionHrefInNewTab(item.href);
  return (
    <a href={item.href} target={openInNewTab ? "_blank" : undefined} rel={openInNewTab ? "noreferrer" : undefined} className={className}>
      {content}
    </a>
  );
}

export function CrmTaskActionLink({ action, compact = false }: { action: CrmTaskAction; compact?: boolean }) {
  const className = cn(
    compact
      ? "inline-flex max-w-48 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors"
      : "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
    getCrmTaskActionToneClasses(action.tone),
    action.disabled ? "cursor-not-allowed opacity-60" : "",
  );
  const content = (
    <>
      <span className="truncate">{action.label}</span>
      <ArrowUpRight className={compact ? "h-3 w-3 shrink-0" : "h-3.5 w-3.5 shrink-0"} />
    </>
  );

  if (action.disabled) {
    if (compact) {
      return (
        <span className={className} title={action.disabledReason ?? action.description ?? undefined} aria-disabled="true">
          {content}
        </span>
      );
    }

    return (
      <span className="inline-flex max-w-56 flex-col gap-1">
        <span className={className} title={action.disabledReason ?? undefined} aria-disabled="true">
          {content}
        </span>
        {action.disabledReason ? (
          <span className="text-[11px] text-[var(--color-muted-foreground)]">{action.disabledReason}</span>
        ) : null}
      </span>
    );
  }

  const hrefKind = getCrmActionHrefKind(action.href);
  if (hrefKind === "internal") {
    return (
      <Link to={action.href} className={className} title={action.description ?? undefined}>
        {content}
      </Link>
    );
  }
  if (!hrefKind) return null;

  const openInNewTab = shouldOpenCrmActionHrefInNewTab(action.href);
  return (
    <a
      href={action.href}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noreferrer" : undefined}
      className={className}
      title={action.description ?? undefined}
    >
      {content}
    </a>
  );
}

export function formatCrmTaskPlaybookKind(kind: CrmTaskPlaybookItemKind): string {
  if (kind === "talking_point") return "Talking point";
  if (kind === "success_criteria") return "Success criteria";
  return formatCrmTaskLabel(kind) ?? kind;
}

function formatCrmTaskLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getCrmTaskProfileSectionSummaryToneClasses(summary: CrmTaskProfileSectionSummary) {
  return getCrmProfileToneClasses(summary.tone);
}

export function getCrmTaskPlaybookToneClasses(tone: CrmTaskPlaybookTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}

function getCrmTaskChecklistToneClasses(tone: CrmTaskChecklistTone) {
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (tone === "info") {
    return "border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 text-[var(--color-primary)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]";
}

function formatCount(count: number, singular: string): string {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

export function formatCrmTaskProfileSectionSummary(summary: CrmTaskProfileSectionSummary): string {
  if (summary.attentionItemCount > 0) {
    return `Evidence: ${summary.primaryLabel} · ${summary.primaryValue}`;
  }
  return `Evidence: ${formatCount(summary.itemCount, "fact")}`;
}

export function formatCrmTaskPlaybookSummary(summary: CrmTaskPlaybookSummary): string {
  const playbookLabel = summary.playbookCount > 1 ? `${summary.playbookCount} playbooks` : "Playbook";
  const highlights = [
    summary.questionCount > 0 ? formatCount(summary.questionCount, "question") : null,
    summary.objectionCount > 0 ? formatCount(summary.objectionCount, "objection") : null,
    summary.successCriteriaCount > 0 ? formatCount(summary.successCriteriaCount, "success cue") : null,
    summary.talkingPointCount > 0 ? formatCount(summary.talkingPointCount, "talking point") : null,
    summary.handoffCount > 0 ? formatCount(summary.handoffCount, "handoff") : null,
  ].filter((value): value is string => Boolean(value));

  if (highlights.length > 0) {
    return `${playbookLabel}: ${highlights.slice(0, 2).join(" · ")}`;
  }
  return `${playbookLabel}: ${formatCount(summary.itemCount, "prompt")}`;
}

export function formatCrmTaskChecklistSummary(summary: CrmTaskExecutionChecklistSummary): string {
  if (summary.openRequiredItemCount > 0) {
    return `Checklist: ${summary.openRequiredItemCount} required open`;
  }
  if (summary.requiredItemCount > 0) {
    return `Checklist ready: ${summary.requiredItemCount} required`;
  }
  return `Checklist: ${summary.itemCount} checks`;
}

export function getCrmTaskChecklistSummaryToneClasses(summary: CrmTaskExecutionChecklistSummary) {
  if (summary.tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
  if (summary.tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
  }
  if (summary.tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]";
  }
  if (summary.tone === "info") {
    return "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)]";
  }
  return "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted-foreground)]";
}

export function getCrmTaskActionToneClasses(tone: CrmTaskActionTone) {
  if (tone === "primary") {
    return "border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/15";
  }
  if (tone === "success") {
    return "border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)] hover:bg-[var(--color-success)]/15";
  }
  if (tone === "warning") {
    return "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 text-[var(--color-warning)] hover:bg-[var(--color-warning)]/15";
  }
  if (tone === "danger") {
    return "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/15";
  }
  return "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-foreground)] hover:bg-[var(--color-background)]";
}
